import { getAuth, RecaptchaVerifier, ConfirmationResult, PhoneAuthProvider, linkWithCredential } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuth } from "../../../context/authProvider";
import { getAuthErrorMessage, onlyDigits, validatePhoneNumber } from "../../../utils/functions";

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
    confirmationResult: ConfirmationResult;
    verificationId: string;
  }
}

export default function PhoneVerifier({ phoneN, disabled }: { phoneN: string, disabled: boolean }) {

  const { setUserPhoneVerified } = useAuth();

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [otp, setOtp] = useState("");
  const [isStepCode, setIsStepCode] = useState(false);

  const auth = getAuth();
  auth.languageCode = "pt-br";

  useEffect(() => {
    // Inicializa o recaptcha garantindo que o elemento com ID existe
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-anchor', {
        'size': 'invisible'
      });
    }

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        // É importante deletar a referência para não tentar re-renderizar em um elemento destruído
        (window as any).recaptchaVerifier = null;
      }
    };
  }, [auth]);

  const handleVerify = async () => {
    try {
      setLoading(true);
      setFeedback("");

      // revalida o comprimento antes de montar o +55: um numero curto vindo do cadastro
      // antigo falharia no Firebase com um erro generico
      if (!validatePhoneNumber(phoneN)) {
        setFeedback("Telefone inválido. Corrija o número (DDD + celular com 11 dígitos) antes de verificar.");
        return null;
      }

      const appVerifier = window.recaptchaVerifier;
      const formattedPhone = `+55${onlyDigits(phoneN)}`;

      // garantir que captcha está ativo
      await appVerifier.render();
      const provider = new PhoneAuthProvider(auth);

      const verificationId = await provider.verifyPhoneNumber(
        formattedPhone,
        appVerifier
      )

      window.verificationId = verificationId
      setIsStepCode(true);
    } catch (error: any) {
      console.log(error)
      setFeedback(getAuthErrorMessage(error?.code, "Falha ao enviar SMS. Tente novamente mais tarde."));
    } finally {
      setLoading(false);
    }
  };

  // 1. Função de Confirmação
  const handleConfirmCode = async () => {
    try {
      setFeedback("");
      setLoading(true);
      const credential = PhoneAuthProvider.credential(
        window.verificationId,
        otp
      )
      const user = auth.currentUser;

      await linkWithCredential(user!, credential)

      const updateOp = await setUserPhoneVerified();
      if (!updateOp) {
        setFeedback("Erro. Tente novamente mais tarde.");
        return null;
      }

    } catch (error: any) {
      console.error("Erro ao validar código: ", error);
      // distingue "codigo errado" de "numero ja vinculado a outra conta" /
      // "esta conta ja tem telefone vinculado", que caiam todos no mesmo texto enganoso
      setFeedback(getAuthErrorMessage(error?.code, "Código inválido. Verifique e tente novamente."));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = () => {
    window.location.reload();
  }

  return (
    <div className="w-fit m-auto">
      <div id="recaptcha-anchor"></div>
      {
        isStepCode ?
          <div>
            <p className="text-center">Digite o código enviado por SMS:</p>
            <input type="text" maxLength={6} placeholder="000000" value={otp} onChange={(e) => setOtp(e.target.value)} className="border my-2 p-2 text-center text-lg tracking-widest rounded m-auto block" />

            <button disabled={loading || otp.length < 6} onClick={handleConfirmCode} className="block m-auto my-2 bg-green-600 text-white px-4 py-2 rounded disabled:bg-gray-400">
              {loading ? "Validando..." : "Confirmar Código"}
            </button>
            <button onClick={resendCode} className="block m-auto my-2 text-sm text-gray-500 underline">
              Alterar número
            </button>

          </div>
          :
          <button disabled={loading || disabled || isStepCode} onClick={handleVerify} className="block m-auto bg-secondary text-white px-4 py-2 rounded disabled:bg-gray-400 disabled:text-white">
            {loading ? "Enviando..." : "Verificar Telefone"}
          </button>
      }
      {feedback && <p className="mt-2 text-sm text-center">{feedback}</p>}
    </div>
  );
}